// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyMeterRow = {
    key: string;
    label: string;
    value: number;
    /** Renders the row in the "bad" tone — used for failing outcomes and statuses. */
    isBad?: boolean;
};

type Props = {
    rows: TyMeterRow[];
    emptyLabel: string;
    /** Caps how many rows are drawn; the rest are summarised as "+N more". */
    limit?: number;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * A breakdown by category — outcome, method, status. Bars rather than a line: these keys have no
 * order between them, and a line drawn across them would invent a trend that does not exist.
 */
export default function MeterList({ rows, emptyLabel, limit = 8 }: Props) {
    if (!rows.length) return <p className="meter-list-empty">{emptyLabel}</p>;

    const peak = Math.max(...rows.map((row) => row.value), 1);
    const shown = rows.slice(0, limit);
    const hidden = rows.length - shown.length;

    return (
        <div className="meter-list">
            {shown.map((row) => (
                <div key={row.key} className="meter-list-row">
                    <span className="meter-list-label">{row.label}</span>
                    {/* Drawn as SVG rather than a styled div: the width is data, and this repo keeps
                        inline style out of the layout layer. `preserveAspectRatio` lets one unit of
                        the viewBox stretch to one percent of whatever width the row ends up at. */}
                    <svg className="meter-list-track" viewBox="0 0 100 8" preserveAspectRatio="none" role="presentation">
                        <rect className="meter-list-bed" x="0" y="0" width="100" height="8" rx="4" />
                        <rect
                            className="meter-list-fill"
                            data-tone={row.isBad ? "bad" : "good"}
                            x="0"
                            y="0"
                            width={(row.value / peak) * 100}
                            height="8"
                            rx="4"
                        />
                    </svg>
                    <span className="meter-list-value">{row.value.toLocaleString()}</span>
                </div>
            ))}

            {hidden > 0 && <p className="meter-list-more">+{hidden}</p>}
        </div>
    );
}
